/**
 * The period-equivalence banner.
 *
 * This sits at the top of Compare and not in a help page, because the mistake
 * it prevents is one a careful person makes while doing exactly the right
 * thing. The NSW public Award caps classroom teaching at 28 periods. Copy that
 * number into a claim and it reads as a protection. At this school it is 1,400
 * minutes a week against the public system's 1,120 — four hours and forty
 * minutes more teaching, conceded in the act of asking for parity, and the
 * employer could accept it word for word.
 *
 * The banner is open by default and dismissible for the session rather than
 * permanently. Someone reading comparator prose about periods needs the unit in
 * front of them at that moment, not to have agreed once that they understood it.
 *
 * The quote is never rewritten. Conversion is derived and rendered beside it.
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';

export function EquivalenceBanner() {
  const [open, setOpen] = useState(true);
  const equivalence = useQuery({ queryKey: ['equivalence'], queryFn: api.equivalence });
  const data = equivalence.data;

  if (!data || data.rows.length === 0) return null;

  const categories = [...new Set(data.rows.map((r) => r.category ?? 'Load'))];

  return (
    /*
      Inverted, which the handoff specifies for this one element — "at the top,
      the period-equivalence banner (inverted, #2a2a28)" — and which is the
      point of it. It sat on --color-subtle, a shade off the paper behind it,
      so the one block on Compare that exists to stop a person copying a number
      was the quietest thing above the fold.
    */
    <section
      className="mb-6 rounded-[8px]"
      style={{ background: 'var(--color-ink)', color: 'var(--color-paper)' }}
      aria-label="Period equivalence"
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="equivalence-grid"
        className="flex w-full items-baseline justify-between gap-4 px-3.5 py-3 text-left"
      >
        <span>
          {/* The mockup's name for this block. A banner that opens with an
              arithmetic result is a fact; a banner that opens with what the
              fact is about is a warning, and this is a warning. */}
          <span className="label mb-1 block" style={{ color: 'var(--color-rule-strong)' }}>
            △ A period is not a period
          </span>
          <span className="text-[13px] font-medium">
            A period is {data.theirMinutes} minutes in the {data.theirName ?? 'comparator'}, and{' '}
            {data.ourMinutes} here
          </span>
        </span>
        <span className="label shrink-0" style={{ color: 'var(--color-rule-strong)' }}>
          {open ? 'Hide' : 'Show'} the conversion
        </span>
      </button>

      {open && (
        <div
          id="equivalence-grid"
          className="border-t px-3.5 pb-3.5 pt-3"
          style={{ borderColor: 'var(--color-muted)' }}
        >
          {/*
            On this ground the emphasis is weight, not hue.

            Both figures were coloured — the comparator's in ink, ours in the
            defect red — and neither survives inversion: #2a2a28 on #2a2a28 is
            invisible and #8a4a3a on it is 1.9:1. The two numbers are already
            distinguished by the sentence around them, so the one that costs
            four hours a week takes the weight and the rest of the line steps
            back to the stronger rule grey (7.3:1 here; --color-muted would be
            2.8:1, under the minimum for body text).
          */}
          <p
            className="measure-wide mb-3 text-[13px] leading-relaxed"
            style={{ color: 'var(--color-rule-strong)' }}
          >
            Copying a period count across instruments changes what it means. The{' '}
            {data.rows[0]!.comparatorPeriods}-period cap below is{' '}
            <span style={{ color: 'var(--color-paper)' }}>
              {data.rows[0]!.minutes.toLocaleString()} minutes
            </span>{' '}
            in the comparator and{' '}
            <strong style={{ color: 'var(--color-paper)' }}>
              {(data.rows[0]!.comparatorPeriods * data.ourMinutes).toLocaleString()} minutes
            </strong>{' '}
            here — {data.overshootMinutes} minutes ({(data.overshootMinutes / 60).toFixed(1)} hours) more
            teaching a week, conceded by asking for parity in the wrong unit.
          </p>

          {categories.map((category) => {
            const rows = data.rows.filter((r) => (r.category ?? 'Load') === category);
            const basis = rows[0]!.basis;
            return (
              <table key={category} className="mb-3 w-full text-[13px] last:mb-0">
                <caption
                  className="label mb-1.5 text-left text-[11px]"
                  style={{ color: 'var(--color-rule-strong)' }}
                >
                  {category} · {rows[0]!.cite}
                </caption>
                <thead>
                  {/* The rule inside an inverted panel is --color-muted: it is
                      the only one of the six greys that reads as a hairline on
                      ink rather than as a stripe. */}
                  <tr className="border-b" style={{ borderColor: 'var(--color-muted)' }}>
                    <th
                      scope="col"
                      className="label py-1.5 text-left font-normal"
                      style={{ color: 'var(--color-rule-strong)' }}
                    >
                      Role
                    </th>
                    <th
                      scope="col"
                      className="label py-1.5 text-right font-normal"
                      style={{ color: 'var(--color-rule-strong)' }}
                    >
                      Their periods
                    </th>
                    {/* Never "per week" for a figure the comparator states per term. */}
                    <th
                      scope="col"
                      className="label py-1.5 text-right font-normal"
                      style={{ color: 'var(--color-rule-strong)' }}
                    >
                      Minutes / {basis}
                    </th>
                    <th
                      scope="col"
                      className="label py-1.5 text-right font-normal"
                      style={{ color: 'var(--color-rule-strong)' }}
                    >
                      In our {data.ourMinutes}-min periods
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr
                      key={`${category}-${row.role}`}
                      className="border-b last:border-b-0"
                      style={{ borderColor: 'var(--color-muted)' }}
                    >
                      <td className="py-1.5">{row.role}</td>
                      {/* Every figure in tabular mono, as the token set says a
                          figure is set — these four columns are read down, and
                          proportional digits make a column of numbers ragged. */}
                      <td className="clause-number py-1.5 text-right tabular-nums">
                        {row.comparatorPeriods}
                      </td>
                      <td className="clause-number py-1.5 text-right tabular-nums">
                        {row.minutes.toLocaleString()}
                      </td>
                      <td className="clause-number py-1.5 text-right font-medium tabular-nums">
                        {row.ourPeriods}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            );
          })}

          <p className="mt-3 text-[11px] leading-snug" style={{ color: 'var(--color-rule-strong)' }}>
            {data.theirSource ? `Their period length is defined at ${data.theirSource}. ` : ''}
            {data.ourNote ?? ''} Draft claims in minutes, or in our periods after conversion — never
            in the comparator's count.
          </p>
        </div>
      )}
    </section>
  );
}
